import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { showErrorToast } from '../../../../shared/components/ToastUtils'
import {
  IAuthActivityLogModel,
  IAuthActivityLogFilter,
  initialAuthActivityLogFilter,
  ACTION_OPTIONS,
  SUCCESS_OPTIONS,
} from './AuthActivityLogModel'
import { extractAuthActivityLogs, loadAuthActivityLogs } from './Api'
import AuthActivityLogTable from './AuthActivityLogTable'

const PAGE_SIZE_DEFAULT = 10

const AuthActivityLogPage: FC = () => {
  const [logs, setLogs] = useState<IAuthActivityLogModel[]>([])
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState<IAuthActivityLogFilter>(initialAuthActivityLogFilter)

  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT)
  const [sortBy, setSortBy] = useState<string>('loggedAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const fetchLogs = useCallback(async () => {
    setLoading(true)
    try {
      const res = await loadAuthActivityLogs()
      const items = extractAuthActivityLogs(res.data)
      setLogs(items)
    } catch (err) {
      console.error(err)
      showErrorToast('Kullanıcı aktivite logları yüklenemedi')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      if (filter.username.trim()) {
        const term = filter.username.trim().toLowerCase()
        if (!log.username?.toLowerCase().includes(term)) return false
      }
      if (filter.action && log.action !== filter.action) return false
      if (filter.success === 'success' && !log.success) return false
      if (filter.success === 'failure' && log.success) return false

      if (filter.startDate || filter.endDate) {
        const logTime = new Date(log.loggedAt).getTime()
        if (Number.isNaN(logTime)) return false

        if (filter.startDate) {
          const start = new Date(filter.startDate)
          start.setHours(0, 0, 0, 0)
          if (logTime < start.getTime()) return false
        }
        if (filter.endDate) {
          const end = new Date(filter.endDate)
          end.setHours(23, 59, 59, 999)
          if (logTime > end.getTime()) return false
        }
      }
      return true
    })
  }, [logs, filter])

  const sortedLogs = useMemo(() => {
    const arr = [...filteredLogs]
    if (!sortBy) return arr
    arr.sort((a, b) => {
      const av = (a as any)[sortBy]
      const bv = (b as any)[sortBy]

      if (sortBy === 'loggedAt') {
        const at = new Date(av).getTime()
        const bt = new Date(bv).getTime()
        return sortOrder === 'asc' ? at - bt : bt - at
      }
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortOrder === 'asc' ? av - bv : bv - av
      }
      if (typeof av === 'boolean' && typeof bv === 'boolean') {
        return sortOrder === 'asc'
          ? Number(av) - Number(bv)
          : Number(bv) - Number(av)
      }
      const as = String(av ?? '').toLowerCase()
      const bs = String(bv ?? '').toLowerCase()
      return sortOrder === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as)
    })
    return arr
  }, [filteredLogs, sortBy, sortOrder])

  const totalPages = Math.max(1, Math.ceil(sortedLogs.length / pageSize))
  const safePage = Math.min(currentPage, totalPages - 1)
  const pagedLogs = useMemo(() => {
    const start = safePage * pageSize
    return sortedLogs.slice(start, start + pageSize)
  }, [sortedLogs, safePage, pageSize])

  const stats = useMemo(() => {
    const total = logs.length
    const logins = logs.filter((l) => l.action === 'LOGIN').length
    const logouts = logs.filter((l) => l.action === 'LOGOUT').length
    const successes = logs.filter((l) => l.success).length
    const failures = total - successes
    return { total, logins, logouts, successes, failures }
  }, [logs])

  const handleFilterChange = (key: keyof IAuthActivityLogFilter, value: string) => {
    setFilter((prev) => ({ ...prev, [key]: value }))
    setCurrentPage(0)
  }

  const handleResetFilters = () => {
    setFilter(initialAuthActivityLogFilter)
    setCurrentPage(0)
  }

  const handlePageChange = (page: number) => setCurrentPage(page)

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setCurrentPage(0)
  }

  const handleSort = (column: string, order: 'asc' | 'desc') => {
    setSortBy(column)
    setSortOrder(order)
  }

  return (
    <div className='card'>
      <ToastContainer />

      {/* Header */}
      <div className='card-header border-0 pt-6 d-flex flex-wrap gap-3 align-items-center justify-content-between'>
        <div className='card-title'>
          <h3 className='card-label fw-bold fs-3 mb-0'>
            <i className='bi bi-shield-lock me-2 text-primary' />
            Kullanıcı Aktivite Logları
          </h3>
          <span className='text-muted fs-7 d-block mt-1'>
            Sisteme giriş ve çıkış kayıtlarını izleyin
          </span>
        </div>
        <div className='d-flex gap-2'>
          <button
            type='button'
            className='btn btn-sm btn-light-primary'
            onClick={fetchLogs}
            disabled={loading}
            title='Yenile'
          >
            {loading ? (
              <span className='spinner-border spinner-border-sm me-2' />
            ) : (
              <i className='bi bi-arrow-clockwise me-2' />
            )}
            Yenile
          </button>
        </div>
      </div>

      <div className='card-body pt-4'>
        {/* Stats Cards */}
        <div className='row g-4 mb-6'>
          <StatCard
            color='primary'
            icon='bi-list-ul'
            label='Toplam Kayıt'
            value={stats.total}
          />
          <StatCard
            color='success'
            icon='bi-box-arrow-in-right'
            label='Giriş'
            value={stats.logins}
          />
          <StatCard
            color='info'
            icon='bi-box-arrow-right'
            label='Çıkış'
            value={stats.logouts}
          />
          <StatCard
            color='success'
            icon='bi-check-circle-fill'
            label='Başarılı'
            value={stats.successes}
          />
          <StatCard
            color='danger'
            icon='bi-x-circle-fill'
            label='Başarısız'
            value={stats.failures}
          />
        </div>

        {/* Filters */}
        <div className='card border mb-6'>
          <div className='card-header min-h-50px'>
            <h5 className='card-title fs-6 fw-bold mb-0'>
              <i className='bi bi-funnel me-2 text-primary' />
              Filtrele
            </h5>
          </div>
          <div className='card-body p-5'>
            <div className='row g-4'>
              <div className='col-md-6 col-xl-3'>
                <label className='form-label fw-semibold fs-7'>Kullanıcı Adı</label>
                <div className='input-group'>
                  <span className='input-group-text bg-light border-end-0'>
                    <i className='bi bi-search text-muted' />
                  </span>
                  <input
                    type='text'
                    className='form-control form-control-solid border-start-0'
                    placeholder='Kullanıcı ara...'
                    value={filter.username}
                    onChange={(e) => handleFilterChange('username', e.target.value)}
                  />
                </div>
              </div>
              <div className='col-md-6 col-xl-3'>
                <label className='form-label fw-semibold fs-7'>İşlem</label>
                <select
                  className='form-select form-select-solid'
                  value={filter.action}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                >
                  {ACTION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-md-6 col-xl-3'>
                <label className='form-label fw-semibold fs-7'>Sonuç</label>
                <select
                  className='form-select form-select-solid'
                  value={filter.success}
                  onChange={(e) => handleFilterChange('success', e.target.value)}
                >
                  {SUCCESS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-md-6 col-xl-3 d-flex align-items-end'>
                <button
                  type='button'
                  className='btn btn-light w-100'
                  onClick={handleResetFilters}
                  disabled={loading}
                >
                  <i className='bi bi-arrow-counterclockwise me-2' />
                  Filtreleri Temizle
                </button>
              </div>
              <div className='col-md-6 col-xl-3'>
                <label className='form-label fw-semibold fs-7'>Başlangıç Tarihi</label>
                <input
                  type='date'
                  className='form-control form-control-solid'
                  value={filter.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                />
              </div>
              <div className='col-md-6 col-xl-3'>
                <label className='form-label fw-semibold fs-7'>Bitiş Tarihi</label>
                <input
                  type='date'
                  className='form-control form-control-solid'
                  value={filter.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Result info */}
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <span className='text-muted fs-7'>
            <i className='bi bi-info-circle me-1' />
            <strong>{sortedLogs.length}</strong> kayıt görüntüleniyor
            {sortedLogs.length !== logs.length && (
              <> (toplam {logs.length} kayıttan filtrelendi)</>
            )}
          </span>
        </div>

        <AuthActivityLogTable
          data={pagedLogs}
          loading={loading}
          currentPage={safePage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      </div>
    </div>
  )
}

interface StatCardProps {
  color: 'primary' | 'success' | 'info' | 'danger' | 'warning'
  icon: string
  label: string
  value: number
}

const StatCard: FC<StatCardProps> = ({ color, icon, label, value }) => (
  <div className='col-6 col-md-4 col-xl'>
    <div className={`card border border-${color}-subtle h-100`}>
      <div className='card-body d-flex align-items-center gap-3 p-4'>
        <div
          className={`symbol symbol-45px bg-light-${color} rounded`}
          style={{ flexShrink: 0 }}
        >
          <span className={`symbol-label text-${color}`}>
            <i className={`bi ${icon} fs-2`} />
          </span>
        </div>
        <div className='d-flex flex-column'>
          <span className='fs-2 fw-bolder text-dark lh-1'>{value}</span>
          <span className='text-muted fs-7'>{label}</span>
        </div>
      </div>
    </div>
  </div>
)

export { AuthActivityLogPage }
export default AuthActivityLogPage
